// @ts-strict-ignore
import camelCase from 'lodash/camelCase';
import {clsx} from 'clsx';
import produce from 'immer';
import partition from 'lodash/partition';
import React, {forwardRef, useImperativeHandle, useRef, useState} from 'react';

import {
  EntrySectionUpdates,
  FiltersOperator,
  MetricEntryBehavior,
  QueryFilterEntry,
  RowLabelType,
  SelectorTypeDefinitions,
} from 'common/query-builder/query-entry-section/types';
import * as BookmarkTypes from 'common/types/reports/bookmark';
import {Event, EventItem} from 'common/query-builder/query-block/events-menu/types';
import {MetricType} from 'common/types/reports/bookmark';
import {QueryBlockReact} from 'common/query-builder/query-block';
import {MenuType} from 'common/query-builder/query-block/types';
import {ButtonTheme} from 'common/components/button/constants';
import {SelectorType as QueryBlockSelectorType} from 'common/query-builder/query-block/constants';
import {OverflowControls} from 'common/query-builder/query-entry-section/row-types/new-metric-entry/common/controls';
import {
  ControlBarAction,
  ControlBarActionItem,
  ControlBarActionItems,
} from 'common/query-builder/query-entry-section/constants';
import {ButtonReact} from 'common/components/button';
import {createFirstTimeEventFilter, isFirstTimeEventFilter} from 'common/widgets/property-filter-menu/models/utils';
// eslint-disable-next-line import/no-cycle
import {QueryEntrySectionReact} from 'common/query-builder/query-entry-section';
import {getFilterEntriesFromFilters} from 'iron/common/query-builder/query-entry-section/row-types/filter/utils';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {RowFiltersPrefix} from 'common/query-builder/query-entry-section/components/filters';
import {isXauMathType} from 'common/query-builder/query-builder/util';
import {BEHAVIOR_PROPERTIES} from 'common/query-builder/query-block/properties-menu/constants';
import {SvgIconReact} from 'common/components/svg-icon';
import {prompt} from 'common/components/confirm';
import type {
  ActiveMenu,
  InheritedEntryRowProps,
  RowProps,
} from 'iron/common/query-builder/query-entry-section/row-types/new-metric-entry/types/catalog';
import {SVG_ICONS} from 'common/components/svg-icon/icons';
import {PropertySectionItem} from 'common/query-builder/query-block/properties-menu/item-menu-types';
import type {BookmarkDragMetadata} from 'common/report/insights/contexts/drag-drop/types';
import {getEventBehaviorName} from 'common/report/insights/contexts/utils/metric-names';
import {useTranslation} from 'common/i18n/hooks';
import {ReplayPreviewRenderer} from 'common/widgets/replay-preview/vnode';

import {BehaviorSelector} from '../behavior-selector';
import styles from './index.module.styl';

export type EventRowProps = RowProps & {
  additionalOverflowControls?: ControlBarActionItem[];
  behavior: MetricEntryBehavior;
  /** renders a label inside the row icon indicator */
  iconLabel?: string | null;
  /** For styling secondary row like "Add Step" / "Add Event" */
  isSecondaryRow?: boolean;
  /** overrides the selected event name */
  label?: string | null;
  onClose?: (() => void) | null | undefined;
  onDelete?: (() => void) | null | undefined;
  onDuplicate?: (() => void) | null | undefined;
  onSelect?: ((behavior: MetricEntryBehavior) => void) | null | undefined;
  /** renders a label before the event name */
  prefixLabel?: string | null;
  /**
   * Open the query block for event selection.
   */
  openQueryBlock?: boolean;
  /**
   * Show this icon for the row icon.
   */
  rowIcon?: keyof typeof SVG_ICONS | null;
  /** renders a label after the event name */
  suffixLabel?: string | null;

  /** hides all overflow controls for a given event */
  hideControls?: boolean;

  /** Enables cohort filters */
  enableCohortFilters?: boolean;

  /** Injects custom filter options */
  otherFilterOptions?: Array<PropertySectionItem<string>>;
};

/**
 * Updates filters for a given behavior along with the filter determiner
 */
function handleFiltersUpdate(props: {
  behavior: MetricEntryBehavior;
  filters?: PropertyFilter[];
  updatedFiltersOperator?: FiltersOperator | null;
  onSelect: EventRowProps[`onSelect`];
}) {
  const result = produce(props.behavior, (behavior) => {
    behavior.filters = props.filters ?? props.behavior.filters;

    if (props.updatedFiltersOperator) {
      behavior.filtersDeterminer = props.updatedFiltersOperator === FiltersOperator.And ? `all` : `any`;
    }
  });

  props.onSelect(result);
}

export function RowFilters(
  props: InheritedEntryRowProps & {
    /** specifies which drag and drop group this section is attached to */
    dragData?: Partial<BookmarkDragMetadata>;
    behavior: MetricEntryBehavior;
    dataGroupId?: string | null;
    disableCloneEntry?: boolean;
    disabledItems?: Obj;
    eventNames?: string[];
    hideEvents?: boolean;
    enableCohortFilters?: boolean;
    isStaging?: boolean | Obj | null;
    onAbortStage: () => void;
    onSelect: EventRowProps[`onSelect`];
    otherFilterOptions?: EventRowProps[`otherFilterOptions`];
  },
) {
  const filtersOperator = props.behavior.filtersDeterminer === `all` ? FiltersOperator.And : FiltersOperator.Or;
  const [firstTimeFilters, regularFilters] = partition(props.behavior.filters, isFirstTimeEventFilter);

  /** renders the AND/OR filter operator selector */
  const conjunctionRenderer = ({index: filterIndex, entry: filterEntry}) =>
    filterIndex === 0 &&
    regularFilters.length > 1 && (
      <RowFiltersPrefix
        filterIndex={filterIndex}
        filter={new PropertyFilter((filterEntry as QueryFilterEntry).event)}
        filtersOperator={props.behavior.filtersDeterminer === `all` ? FiltersOperator.And : FiltersOperator.Or}
        isReadOnlyMode={false}
        forNewUI
        handleChange={(item) =>
          handleFiltersUpdate({
            behavior: props.behavior,
            updatedFiltersOperator: item.value as FiltersOperator,
            onSelect: props.onSelect,
          })
        }
      />
    );
  return (
    <QueryEntrySectionReact
      dragData={{
        ...props.dragData,
        type: `inline-filter`,
      }}
      containedSection
      indentFilters
      elref={camelCase(`entry-filters`)}
      autoactivatePlaceholders
      enableAllDataGroupsForScdProps={props.sectionProps.enableAllDataGroupsForScdProps}
      excludeDimensionProperties={props.sectionProps.excludeDimensionProperties}
      includeCustomProperties={props.sectionProps.includeCustomProperties}
      showBorrowedProperties={props.sectionProps.showBorrowedProperties}
      hideEditControls={props.sectionProps.hideEditControls}
      propertyCombineMode={props.sectionProps.propertyCombineMode ?? undefined}
      eventNames={props.eventNames ?? []}
      chartType={props.currentChartType}
      supportB2bCohorts={props.sectionProps.dataGroupsEnabled}
      dataGroupsEnabled={props.sectionProps.dataGroupsEnabled}
      projectId={props.sectionProps.projectId}
      workspaceId={props.sectionProps.workspaceId}
      dataGroupId={props.dataGroupId}
      disableAddEntry
      rowTypes={[SelectorTypeDefinitions.Filter]}
      rowLabelType={RowLabelType.None}
      hideEvents={props.hideEvents ?? false}
      otherFilterOptions={props.otherFilterOptions ?? props.sectionProps.otherFilterOptions ?? []}
      projectHasCohorts={props.enableCohortFilters}
      projectHasPeople
      disabledItems={props.disabledItems ?? {}}
      rowDraggable
      managedDragAndDrop
      trackProperties={{sectionName: `inline-filter`}}
      disableCloneEntry={props.disableCloneEntry ?? false}
      entries={getFilterEntriesFromFilters([...regularFilters, ...firstTimeFilters], filtersOperator)}
      sectionName={`inline-filter`}
      renderSuffix={regularFilters.length > 1 ? conjunctionRenderer : undefined}
      onUpdate={(updates: EntrySectionUpdates<QueryFilterEntry>) => {
        handleFiltersUpdate({
          behavior: props.behavior,
          filters: updates.entries.map((entry) => entry.event as PropertyFilter),
          onSelect: props.onSelect,
        });
      }}
      stagingRow={props.isStaging}
      onAbortStage={() => props.onAbortStage()}
    />
  );
}

export type BehaviorRowProps = EventRowProps & {
  metricIndex?: number;
  behaviorIndex?: BookmarkDragMetadata[`behaviorIndex`];
  /** when provided, allows for a drag and drop gripper element */
  handleRef?: (element: Element | null) => void;
  dropRef?: (element: Element | null) => void;

  stagedFilter?: Obj | null;
  abortStagedFilter?: () => void;

  showAllEvents?: boolean;
  showAnyEvent?: boolean;
};

/**
 * Provides a component for rendering a behavior event selection together with filters.
 *
 * @todo(metrics) filters are not matching designs
 */
export const BehaviorEventRow = forwardRef<HTMLDivElement, BehaviorRowProps>(function (props, ref) {
  const {print} = useTranslation();
  const entryElRef = useRef<HTMLDivElement>(null);
  const [isStagingFilter, setIsStagingFilter] = useState(false);
  const hasFirstTimeFilter = props.behavior.filters?.some(isFirstTimeEventFilter);

  const handleAddFilter = () => setIsStagingFilter(true);
  const handleDelete = () => props.onDelete();
  const handleDuplicate = () => props.onDuplicate();

  const handleEventSelected = (selectedEvent: EventItem) => {
    const result = produce(props.behavior, (behavior) => {
      behavior.name = selectedEvent.value;
      behavior.id = selectedEvent.id ?? null;
      behavior.type = selectedEvent.custom ? MetricType.CustomEvent : MetricType.Event;
    });

    setIsStagingFilter(false);
    props.onSelect(result);
  };

  const handleAddFirstTimeFilter = () =>
    !hasFirstTimeFilter &&
    handleFiltersUpdate({
      filters: [...props.behavior.filters, createFirstTimeEventFilter()],
      behavior: props.behavior,
      onSelect: props.onSelect,
    });

  const handleRename = async () => {
    const promptResponse = await prompt(
      {
        'initial-input-value': print(getEventBehaviorName(props.behavior as BookmarkTypes.Behavior)) ?? ``,
        'allow-empty-input-value': true,
        'show-input': true,
      },
      {},
      {
        title: ({t}) => t(`query-builder.actions.rename`),
        confirmButtonLabel: ({t}) => t(`common.done`),
      },
    );

    if (promptResponse.didConfirm) {
      const name = promptResponse.inputValue.trim();

      props.onSelect(
        produce(props.behavior, (behavior) => {
          behavior.renamed = name || undefined;
        }),
      );
    }
  };

  // expose the entry element to the parent component
  useImperativeHandle(ref, () => entryElRef.current!, []);

  const hasActiveOverflow =
    props.activeMenu?.key === String(props.behaviorIndex ?? props.index) &&
    props.activeMenu?.menu === `behavior-entry-menu`;

  const setActiveMenu = (menu: ActiveMenu | null) =>
    props.setActiveMenu(menu ? {key: String(props.behaviorIndex ?? props.index), menu} : null);

  // for enabling session replay preview in context panels
  const replayPreviewRender = ReplayPreviewRenderer;

  return (
    <div
      ref={props.dropRef}
      className={clsx(styles.eventRow, {
        [styles.secondaryEventRow]: props.isSecondaryRow,
        [styles.isActive]: hasActiveOverflow,
      })}
    >
      {/* row selector and actions */}
      <div ref={entryElRef} className={styles.header}>
        {props.handleRef && (
          <div className={styles.gripper} ref={props.handleRef}>
            <SvgIconReact icon={`gripper-xs`} />
          </div>
        )}

        <div className={styles.rowIcon}>
          <SvgIconReact icon={props.rowIcon ?? `behavior-event`} />
          <span>{props.iconLabel}</span>
        </div>

        {/* renders the actual event selection menu */}
        <div className={styles.content}>
          <QueryBlockReact
            disableContextPanel={props.isSecondaryRow}
            type={MenuType.Events}
            showSessionEvent
            showAllEvents={props.showAllEvents}
            showAnyEvent={props.showAnyEvent}
            selectorIcon=''
            selectorType={QueryBlockSelectorType.Button}
            theme={ButtonTheme.QueryBuilder}
            label={props.label}
            disabled={props.sectionProps.disabled}
            userId={props.sectionProps.userId}
            workspaceId={props.sectionProps.workspaceId}
            projectId={props.sectionProps.projectId}
            prefixLabel={props.prefixLabel}
            suffixLabel={props.suffixLabel}
            contextPanelParentElement={entryElRef.current}
            selected={
              {
                label: null,
                renamed: props.behavior.renamed,
                id: props.behavior.id,
                value: props.behavior.name,
                custom: props.behavior.type === MetricType.CustomEvent,
              } satisfies Event
            }
            onClose={() => {
              props.onClose?.();
              setActiveMenu(null);
            }}
            onOpen={() => setActiveMenu(`behavior-entry-menu`)}
            onSelect={(detail) => handleEventSelected(detail.item)}
            open={props.openQueryBlock ?? undefined}
            replayPreviewRenderer={replayPreviewRender}
          />
        </div>

        {/* overflow controls for each individual event in a behavior */}
        {!props.hideControls && (
          <div className={clsx(styles.actions, {[styles.isOpen]: hasActiveOverflow})}>
            <ButtonReact theme={ButtonTheme.Tertiary} icon='filter-xs' onClick={handleAddFilter} />
            {props.onDelete && <ButtonReact theme={ButtonTheme.Tertiary} icon='x' onClick={handleDelete} />}
            <OverflowControls
              onMenuChange={(isOpen) => setActiveMenu(isOpen ? `behavior-entry-menu` : null)}
              items={[
                {
                  items: [
                    /** stage filter */
                    {...ControlBarActionItems[ControlBarAction.Filter], onSelect: handleAddFilter},

                    /** events can only have one first time filter */
                    !hasFirstTimeFilter && {
                      ...ControlBarActionItems[ControlBarAction.FirstTimeFilter],
                      onSelect: handleAddFirstTimeFilter,
                    },

                    // rename event
                    {...ControlBarActionItems[ControlBarAction.Rename], onSelect: handleRename},

                    /** delete event */
                    props.onDelete && {...ControlBarActionItems[ControlBarAction.Delete], onSelect: handleDelete},

                    /** duplicate event */
                    props.onDuplicate && {
                      ...ControlBarActionItems[ControlBarAction.Duplicate],
                      onSelect: handleDuplicate,
                    },

                    /** passes in any extract overflow action */
                    ...(props.additionalOverflowControls || []),
                  ].filter(Boolean),
                },
              ]}
            />
          </div>
        )}
      </div>

      {/* renders filters for the behavior event */}
      <div className={styles.filters}>
        <RowFilters
          {...props}
          dragData={{
            // makes filters local to each behavior event
            group: `${props.entry._idx}-${props.behaviorIndex ?? props.index}-inline-filters`,
            metricIndex: props.metricIndex,
            behaviorIndex: props.behaviorIndex,
            parentMetricIndex: props.parentMetricIndex,
          }}
          behavior={props.behavior}
          isStaging={props.stagedFilter ?? isStagingFilter}
          /** prevents behavioral or computed filters from showing up */
          otherFilterOptions={props.otherFilterOptions}
          /** makes sure only properties related to the event show up in filters */
          eventNames={[props.behavior.name]}
          enableCohortFilters={props.enableCohortFilters}
          disabledItems={
            isXauMathType(props.entry.measurement.aggregationOperator)
              ? Array.from(BEHAVIOR_PROPERTIES.keys()).reduce((acc, key) => {
                  acc[key] = true;
                  return acc;
                }, {})
              : {}
          }
          dataGroupId={
            props.entry.measurement.dataGroupId ?? props.entry.behavior.dataGroupId ?? props.sectionProps.dataGroupId
          }
          onAbortStage={() => {
            setIsStagingFilter(false);
            props.abortStagedFilter?.();
          }}
          onSelect={props.onSelect}
        />
      </div>
    </div>
  );
});

/**
 * Provides a component for rendering a profile based behavior selection together with filters.
 *
 * @todo(metrics) filters are not matching designs
 */
export const BehaviorProfileRow = forwardRef<HTMLDivElement, BehaviorRowProps>(function (props, ref) {
  const entryElRef = useRef<HTMLDivElement>(null);
  const [isStagingFilter, setIsStagingFilter] = useState(false);

  const handleAddFilter = () => setIsStagingFilter(true);

  // expose the entry element to the parent component
  useImperativeHandle(ref, () => entryElRef.current!, []);

  return (
    <div
      ref={props.dropRef}
      className={clsx(styles.eventRow, {
        [styles.secondaryEventRow]: props.isSecondaryRow,
        [styles.isActive]: props.activeMenu?.menu === `behavior-entry-menu`,
      })}
    >
      {/* row selector and actions */}
      <div ref={entryElRef} className={styles.header}>
        {props.handleRef && (
          <div className={styles.gripper} ref={props.handleRef}>
            <SvgIconReact icon={`gripper-xs`} />
          </div>
        )}

        <div className={styles.rowIcon}>
          <SvgIconReact icon={props.rowIcon ?? `behavior-event`} />
          <span>{props.iconLabel}</span>
        </div>

        {/* renders the actual selection menu */}
        <div className={styles.content}>
          <BehaviorSelector
            {...props}
            onClose={() => {
              props.onClose?.();
              props.setActiveMenu(null);
            }}
            onOpen={() => props.setActiveMenu({menu: `behavior-entry-menu`})}
          />
        </div>

        {/* overflow controls for each individual sub-behavior in a behavior */}
        {!props.hideControls && (
          <div className={styles.actions}>
            <ButtonReact theme={ButtonTheme.Tertiary} icon='filter-xs' onClick={handleAddFilter} />
          </div>
        )}
      </div>

      {/* renders filters for the sub-behavior */}
      <div className={styles.filters}>
        <RowFilters
          {...props}
          dragData={{
            // makes filters local to each sub-behavior
            group: `${props.entry._idx}-${props.behaviorIndex ?? props.index}-inline-filters`,
            metricIndex: props.index,
            parentMetricIndex: props.parentMetricIndex,
          }}
          enableCohortFilters
          behavior={props.entry.behavior}
          isStaging={isStagingFilter}
          dataGroupId={
            props.entry.measurement.dataGroupId ?? props.entry.behavior.dataGroupId ?? props.sectionProps.dataGroupId
          }
          onAbortStage={() => setIsStagingFilter(false)}
          onSelect={props.onSelect}
          hideEvents
        />
      </div>
    </div>
  );
});

type EventRowSkeletonProps = Omit<BehaviorRowProps, `behavior`> & {
  activeMenuType?: ActiveMenu;
  contentEl: React.ReactNode;
  actionsEl?: React.ReactNode;
  filtersEl?: React.ReactNode;
};

/**
 * Layout component for a row inside a metric for selecting or adding a selection
 */
export const EventRowSkeleton = forwardRef<HTMLDivElement, EventRowSkeletonProps>(function (props, ref) {
  const entryElRef = useRef<HTMLDivElement>(null);

  // expose the entry element to the parent component
  useImperativeHandle(ref, () => entryElRef.current!, []);

  return (
    <div
      ref={props.dropRef}
      className={clsx(styles.eventRow, {
        [styles.secondaryEventRow]: props.isSecondaryRow,
        [styles.isActive]: props.activeMenu?.menu === props.activeMenuType,
      })}
    >
      {/* row selector and actions */}
      <div ref={entryElRef} className={styles.header}>
        {props.handleRef && (
          <div className={styles.gripper} ref={props.handleRef}>
            <SvgIconReact icon={`gripper-xs`} />
          </div>
        )}

        <div className={styles.rowIcon}>
          <SvgIconReact icon={props.rowIcon ?? `behavior-event`} />
          <span>{props.iconLabel}</span>
        </div>

        {/* renders the actual selection menu */}
        <div className={styles.content}>{props.contentEl}</div>

        {/* overflow controls for each individual sub-behavior in a behavior */}
        {props.actionsEl}
      </div>

      {/* renders filters for the sub-behavior */}
      {props.filtersEl && <div className={styles.filters}>{props.filtersEl}</div>}
    </div>
  );
});
